/**
 * Icon — the single icon primitive for the GardEn design system
 * (design-revamp / handoff-2026-06).
 *
 * The prototype (`docs/GardEn/handoff-2026-06/garden/ui.jsx`) ships a
 * hand-rolled `GI` map of raw SVG path strings rendered by an inline
 * `<Icon>`. Guardrails forbid inline custom SVG paths in production
 * (REQ-PRD-§11.2-C / repo rule) — every glyph here maps to a
 * `lucide-react` component instead. The exported `IconName` union is the
 * contract the rest of the library + the screens depend on: it covers
 * every `name` used across `garden/ui.jsx` and `garden/screens-*.jsx`.
 *
 * Sizing + stroke mirror the prototype defaults (`size=20`,
 * `stroke=1.75`). The icon is `aria-hidden` — it is always decorative;
 * the adjacent label carries the meaning (WCAG AA, matching the
 * GConfidence/GPill idiom).
 */
import {
  BookOpen,
  Sun,
  Moon,
  Globe,
  Download,
  Trash2,
  LogOut,
  Info,
  TriangleAlert,
  Star,
  LayoutGrid,
  PaintBucket,
  Zap,
  Layers,
  DoorOpen,
  Droplet,
  Plus,
  Send,
  Clock,
  ShieldCheck,
  House,
  MessageCircle,
  MapPin,
  Menu,
  Search,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Bell,
  Check,
  CircleCheck,
  X,
  Paperclip,
  Camera,
  Mic,
  Sparkles,
  Phone,
  User,
  type LucideIcon,
} from "lucide-react";
import type { ReactElement } from "react";

/**
 * Design name → lucide component. Several design names are aliases for
 * the same lucide glyph (the prototype distinguishes them semantically,
 * not visually): `tile`/`grid` → LayoutGrid, `electrics`/`bolt` → Zap,
 * `rough`/`layers` → Layers, `doors`/`door` → DoorOpen,
 * `plumbing`/`drop` → Droplet, `warn` → TriangleAlert,
 * `checkCircle` → CircleCheck.
 *
 * This map is the single source of truth: {@link IconName} is derived from
 * its keys, and {@link ICON_NAMES} is its runtime key set (used by GPill to
 * tell an icon-name string from an arbitrary `ReactNode` leading slot).
 */
const nameToIcon = {
  home: House,
  grid: LayoutGrid,
  msg: MessageCircle,
  pin: MapPin,
  book: BookOpen,
  menu: Menu,
  search: Search,
  arrowR: ArrowRight,
  arrowUp: ArrowUp,
  chevR: ChevronRight,
  chevL: ChevronLeft,
  chevD: ChevronDown,
  bell: Bell,
  check: Check,
  checkCircle: CircleCheck,
  x: X,
  clip: Paperclip,
  camera: Camera,
  mic: Mic,
  sparkles: Sparkles,
  phone: Phone,
  user: User,
  sun: Sun,
  moon: Moon,
  globe: Globe,
  download: Download,
  trash: Trash2,
  logout: LogOut,
  info: Info,
  warn: TriangleAlert,
  star: Star,
  tile: LayoutGrid,
  paint: PaintBucket,
  electrics: Zap,
  rough: Layers,
  doors: DoorOpen,
  plumbing: Droplet,
  plus: Plus,
  send: Send,
  clock: Clock,
  shield: ShieldCheck,
  layers: Layers,
  bolt: Zap,
  door: DoorOpen,
  drop: Droplet,
} satisfies Record<string, LucideIcon>;

/**
 * Every icon name referenced by the prototype component library
 * (`garden/ui.jsx` → `GI`) and the reference screens
 * (`garden/screens-*.jsx`). Derived from {@link nameToIcon} so the type and
 * the map can never drift.
 */
export type IconName = keyof typeof nameToIcon;

/**
 * Runtime set of valid icon names — lets callers (e.g. GPill) narrow a
 * `string` leading slot to an {@link IconName} without re-listing the union.
 */
export const ICON_NAMES: ReadonlySet<IconName> = new Set(
  Object.keys(nameToIcon) as IconName[],
);

/** Type guard: is `value` a known {@link IconName}? */
export function isIconName(value: unknown): value is IconName {
  return typeof value === "string" && ICON_NAMES.has(value as IconName);
}

export interface IconProps {
  /** Design icon name (see {@link IconName}). */
  name: IconName;
  /** Square pixel size. Defaults to 20 (prototype default). */
  size?: number;
  /** Stroke width. Defaults to 1.75 (prototype default). */
  stroke?: number;
  /** Additional classes (typically a `text-*` token utility — the icon
   * inherits `currentColor`). */
  className?: string;
}

/**
 * Renders a lucide icon for the given design `name`.
 *
 * Always decorative: `aria-hidden="true"` and `focusable={false}`. Color
 * comes from the surrounding `currentColor` (set a `text-*` token utility
 * on this element or an ancestor). `data-testid="garden-icon"` plus
 * `data-icon={name}` make the glyph addressable in tests without leaking
 * the underlying lucide component name.
 */
export function Icon({
  name,
  size = 20,
  stroke = 1.75,
  className,
}: IconProps): ReactElement {
  const LucideGlyph = nameToIcon[name];
  return (
    <LucideGlyph
      data-testid="garden-icon"
      data-icon={name}
      aria-hidden="true"
      focusable={false}
      size={size}
      strokeWidth={stroke}
      className={className}
    />
  );
}
